import WaButton from "@awesome.me/webawesome-pro/dist/react/button/index.js";
import WaCallout from "@awesome.me/webawesome-pro/dist/react/callout/index.js";
import WaCard from "@awesome.me/webawesome-pro/dist/react/card/index.js";
import WaDetails from "@awesome.me/webawesome-pro/dist/react/details/index.js";
import WaIcon from "@awesome.me/webawesome-pro/dist/react/icon/index.js";
import WaSpinner from "@awesome.me/webawesome-pro/dist/react/spinner/index.js";
import type { FeatureCollection } from "geojson";
import React, { useCallback, useMemo, useState } from "react";

import { trackEvent } from "../../analytics/plausible";
import { estimateRoute } from "../../api/estimateClient";
import { DepartureSelector } from "../../components/DepartureSelector";
import { DesktopJourneyFareLayout } from "../../components/desktop/DesktopJourneyFareLayout";
import { EstimateNotice } from "../../components/EstimateNotice";
import { GoogleAddressFinder } from "../../components/GoogleAddressFinder";
import { isPointInsideBoundary } from "../../components/JourneyMap/helpers/camera";
import { PassengerCountSelector } from "../../components/PassengerCountSelector";
import { PricingBreakdown } from "../../components/PricingBreakdown";
import { HowFaresAreCalculatedNotice } from "../../components/shared/HowFaresAreCalculatedNotice";
import { JourneyMapCard } from "../../components/shared/JourneyMapCard";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useJourneyStore } from "../../store/useJourneyStore";
import type { EstimateResponse, LatLng } from "../../types/journey";
import { getNowForDateTimeLocal, isPastDateTimeLocal } from "../../utils/time";

const ORIGIN_OUTSIDE_MSG = "Origin must be within the BCP boundary.";
const SAME_LOCATION_MSG = "Origin and destination cannot be the same.";
const PAST_DEPARTURE_MSG = "Planned departure time can’t be in the past.";

const MOBILE_BREAKPOINT_PX = 720;

function distanceBand(miles: number) {
  if (miles < 2) return "0-2 Miles";
  if (miles < 5) return "2-5 Miles";
  if (miles < 10) return "5-10 Miles";
  if (miles < 20) return "10-20 Miles";
  return "20+ Miles";
}

function priceBand(price: number) {
  if (price < 5) return "£0–£5";
  if (price < 10) return "£5–£10";
  if (price < 20) return "£10–£20";
  if (price < 30) return "£20–£30";
  if (price < 40) return "£30–430";
  if (price < 50) return "£40–£50";
  if (price < 60) return "£50–£60";
  if (price < 70) return "£60–£70";
  if (price < 80) return "£70–£80";

  return "£80+";
}

function samePoint(a?: LatLng | null, b?: LatLng | null) {
  if (!a || !b) return false;
  const eps = 0.00002; // ~2m
  return Math.abs(a.lat - b.lat) < eps && Math.abs(a.lng - b.lng) < eps;
}

const shortLabel = (s: string) => (s ? s.split(",")[0].trim() : "");

const Dashboard: React.FC = () => {
  const {
    origin,
    destination,
    estimate,
    loading,
    error,
    setOrigin,
    setDestination,
    setEstimate,
    setLoading,
    setError,
  } = useJourneyStore();

  const [originText, setOriginText] = useState("");
  const [destinationText, setDestinationText] = useState("");
  const [boundary, setBoundary] = useState<FeatureCollection | null>(null);

  const [originBoundaryError, setOriginBoundaryError] = useState<string | null>(null);
  const [sameLocationError, setSameLocationError] = useState<string | null>(null);
  const [travelTimeError, setTravelTimeError] = useState<string | null>(null);

  const [leaveNow, setLeaveNow] = useState(true);
  const [travelTime, settravelTime] = useState("");
  const [passengerCount, setPassengerCount] = useState(1);
  const [isEditingOrigin, setIsEditingOrigin] = useState(false);
  const [isEditingDestination, setIsEditingDestination] = useState(false);

  const originErrorText = originBoundaryError ?? sameLocationError;
  const destinationErrorText = sameLocationError;

  const hasFieldError = Boolean(originErrorText || destinationErrorText || travelTimeError);
  const mapOrigin = hasFieldError ? null : origin;
  const mapDestination = hasFieldError ? null : destination;
  const [departureSyncTick, setDepartureSyncTick] = useState(0);

  const hasValidDeparture = leaveNow || (!leaveNow && Boolean(travelTime) && !travelTimeError);
  const isMobile = useIsMobile(MOBILE_BREAKPOINT_PX);

  const hasEstimate = Boolean(estimate);

  const [mobileMode, setMobileMode] = useState<"input" | "result">("input");

  const [lastSubmitted, setLastSubmitted] = useState<{
    leaveNow: boolean;
    travelTime: string;
    passengerCount: number;
  } | null>(null);

  React.useEffect(() => {
    if (isMobile && hasEstimate) setMobileMode("result");
    if (isMobile && !hasEstimate) setMobileMode("input");
  }, [isMobile, hasEstimate]);

  const sameOriginDestination = useMemo(
    () => samePoint(origin, destination),
    [origin, destination],
  );

  const validateSameLocation = useCallback(
    (nextOrigin: LatLng | null, nextDestination: LatLng | null) => {
      if (originBoundaryError) {
        setSameLocationError(null);
        return;
      }
      if (samePoint(nextOrigin, nextDestination)) setSameLocationError(SAME_LOCATION_MSG);
      else setSameLocationError(null);
    },
    [originBoundaryError],
  );

  const handleLeaveNowChange = useCallback((v: boolean) => {
    setLeaveNow(v);
    if (v) settravelTime("");
    setTravelTimeError(null);
  }, []);

  const handleTravelTimeChange = useCallback((v: string) => {
    settravelTime(v);

    if (!v) {
      setTravelTimeError(null);
      return;
    }

    if (isPastDateTimeLocal(v)) setTravelTimeError(PAST_DEPARTURE_MSG);
    else setTravelTimeError(null);
  }, []);

  const handlePassengerCountChange = useCallback(
    (v: number) => {
      setPassengerCount(v);
      setEstimate(null);
      setError(null);
      if (isMobile) setMobileMode("input");
    },
    [isMobile, setError, setEstimate],
  );

  const handlePickOrigin = useCallback(
    (picked: { label: string; location: LatLng }) => {
      setError(null);
      setOriginBoundaryError(null);
      setSameLocationError(null);

      if (boundary) {
        const ok = isPointInsideBoundary(boundary, picked.location);
        trackEvent("origin_selected", { within_boundary: ok });

        if (!ok) {
          setOrigin(null);
          setEstimate(null);
          setOriginText(picked.label);
          setOriginBoundaryError(ORIGIN_OUTSIDE_MSG);
          return;
        }
      }

      setOrigin(picked.location);
      setOriginText(picked.label);
      setEstimate(null);

      if (isMobile) setMobileMode("input");

      validateSameLocation(picked.location, destination);
    },
    [boundary, destination, isMobile, setError, setEstimate, setOrigin, validateSameLocation],
  );

  const handlePickDestination = useCallback(
    (picked: { label: string; location: LatLng }) => {
      setError(null);
      setSameLocationError(null);

      setDestination(picked.location);
      setDestinationText(picked.label);
      setEstimate(null);

      if (isMobile) setMobileMode("input");

      validateSameLocation(origin, picked.location);
      trackEvent("destination_selected");
    },
    [
      origin,
      isMobile,
      setDestination,
      setDestinationText,
      setError,
      setEstimate,
      validateSameLocation,
    ],
  );

  const handleEstimate = useCallback(async () => {
    if (originBoundaryError) {
      trackEvent("validation_error", { error_kind: "origin_outside_boundary" });
      return;
    }

    if (sameOriginDestination) {
      setSameLocationError(SAME_LOCATION_MSG);
      trackEvent("validation_error", { error_kind: "origin_equals_destination" });
      return;
    }

    if (!leaveNow && travelTime) {
      if (isPastDateTimeLocal(travelTime)) {
        setTravelTimeError(PAST_DEPARTURE_MSG);
        trackEvent("validation_error", { error_kind: "departure_in_past" });
        return;
      }
    }

    if (!origin || !destination) {
      trackEvent("validation_error", { error_kind: "missing_fields" });
      setError("Please select both an origin and destination.");
      return;
    }

    setLastSubmitted({ leaveNow, travelTime, passengerCount });

    trackEvent("estimate_clicked", {
      leave_now: leaveNow,
      planned_time_set: !leaveNow && Boolean(travelTime),
      passenger_count: passengerCount,
      passenger_group: passengerCount > 4 ? "5_8" : "1_4",
    });

    setLoading(true);
    setError(null);

    try {
      const data: EstimateResponse = await estimateRoute({
        origin,
        destination,
        leaveNow,
        passengerCount,
        ...(leaveNow || !travelTime ? {} : { travelTime }),
      });

      setEstimate(data);

      if (isMobile) setMobileMode("result");

      trackEvent("estimate_success", {
        tariff: data.pricing.tariffCode,
        has_traffic_delay: (data.route.trafficDelaySeconds ?? 0) > 0,
        distance_band: distanceBand(data.route.distanceMiles),
        price_band: priceBand(data.pricing.total),
        passenger_count: data.passengerCount ?? passengerCount,
        passenger_group: (data.passengerCount ?? passengerCount) > 4 ? "5_8" : "1_4",
      });
    } catch (e) {
      console.error(e);
      setEstimate(null);
      setError("Something went wrong.");
      trackEvent("estimate_error", { error_kind: "api" });
    } finally {
      setLoading(false);
    }
  }, [
    destination,
    origin,
    originBoundaryError,
    sameOriginDestination,
    leaveNow,
    travelTime,
    passengerCount,
    isMobile,
    setError,
    setEstimate,
    setLoading,
  ]);

  const handleEditJourney = useCallback(() => {
    if (lastSubmitted) {
      setLeaveNow(lastSubmitted.leaveNow);
      settravelTime(lastSubmitted.travelTime);
      setPassengerCount(lastSubmitted.passengerCount);
      setTravelTimeError(null);
    }

    // force DepartureSelector to re-sync the web component when input screen appears
    setDepartureSyncTick(t => t + 1);

    setMobileMode("input");
  }, [lastSubmitted]);

  const InputsContent = (
    <div className="wa-stack wa-gap-m">
      <div className="wa-stack">
        <h2 className="wa-heading-l" style={{ pointerEvents: "none" }}>
          Taxi Fare Calculator
        </h2>
      </div>

      <GoogleAddressFinder
        label="Origin"
        placeholder="Search an address or postcode…"
        value={originText}
        onValueChange={v => {
          setOriginText(v);
          setOriginBoundaryError(null);
          setSameLocationError(null);
        }}
        onPick={picked => {
          handlePickOrigin(picked);
          setIsEditingOrigin(false);
        }}
        disabled={loading}
        errorText={originErrorText}
        countryCode="gb"
        boundary={boundary}
        enforceBoundary
        outsideBoundaryMessage={ORIGIN_OUTSIDE_MSG}
        biasToBoundary
        onOutsideBoundaryPick={msg => {
          setOrigin(null);
          setEstimate(null);
          setSameLocationError(null);
          setOriginBoundaryError(msg);
          if (isMobile) setMobileMode("input");
        }}
        onBeginEdit={() => {
          setIsEditingOrigin(true);
          setOriginText("");
          setOrigin(null);
          setEstimate(null);
          setOriginBoundaryError(null);
          setSameLocationError(null);
          setError(null);
          if (isMobile) setMobileMode("input");
        }}
        onEndEdit={() => setIsEditingOrigin(false)}
      />

      <GoogleAddressFinder
        label="Destination"
        placeholder="Search an address or postcode…"
        value={destinationText}
        onValueChange={v => {
          setDestinationText(v);
          setSameLocationError(null);
        }}
        onPick={picked => {
          handlePickDestination(picked);
          setIsEditingDestination(false);
        }}
        disabled={loading}
        errorText={destinationErrorText}
        countryCode="gb"
        onBeginEdit={() => {
          setIsEditingDestination(true);
          setDestinationText("");
          setDestination(null);
          setEstimate(null);
          setSameLocationError(null);
          setError(null);
          if (isMobile) setMobileMode("input");
        }}
        onEndEdit={() => setIsEditingDestination(false)}
        boundary={boundary}
        biasToBoundary
      />

      <div style={{ marginTop: 16 }}>
        <PassengerCountSelector
          value={passengerCount}
          onChange={handlePassengerCountChange}
          disabled={loading}
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <DepartureSelector
          leaveNow={leaveNow}
          travelTime={travelTime}
          onLeaveNowChange={handleLeaveNowChange}
          ontravelTimeChange={handleTravelTimeChange}
          disabled={loading}
          minTravelTime={getNowForDateTimeLocal()}
          travelTimeErrorText={travelTimeError}
          syncKey={departureSyncTick}
        />
      </div>

      <WaButton
        variant="brand"
        disabled={
          loading ||
          !!originBoundaryError ||
          !!sameLocationError ||
          !!travelTimeError ||
          !origin ||
          !destination ||
          !hasValidDeparture
        }
        onClick={handleEstimate as any}
        aria-busy={loading}
      >
        {loading ? (
          <span className="wa-cluster" style={{ justifyContent: "center", width: "100%" }}>
            <WaSpinner aria-label="Calculating route" />
          </span>
        ) : (
          "Get Estimate"
        )}
      </WaButton>

      {error && <WaCallout variant="danger">{error}</WaCallout>}
    </div>
  );

  return (
    <div className="wa-stack wa-gap-l">
      <HowFaresAreCalculatedNotice isMobile={isMobile}>
        <EstimateNotice isMobile={isMobile} />
      </HowFaresAreCalculatedNotice>

      <JourneyMapCard
        isMobile={isMobile}
        origin={mapOrigin}
        destination={mapDestination}
        originLabel={originText}
        destinationLabel={destinationText}
        estimate={estimate}
        onBoundaryReady={setBoundary}
        isEditingOrigin={isEditingOrigin}
        isEditingDestination={isEditingDestination}
      />

      {isMobile ? (
        mobileMode === "result" && estimate ? (
          <div className="wa-stack wa-gap-l">
            {/* Summary card (includes Journey header + Edit affordance) */}
            <WaCard className="wa-p-l wa-stack" style={{ height: "100%" }}>
              <PricingBreakdown
                estimate={estimate}
                originLabel={originText}
                destinationLabel={destinationText}
                onEditJourney={handleEditJourney}
                hideBreakdown
              />
            </WaCard>

            {/* Expandable details */}
            <WaDetails open={false} onWaShow={() => trackEvent("view_fare_breakdown")}>
              <span
                slot="summary"
                className="wa-cluster wa-gap-s wa-heading-s"
                style={{ alignItems: "center" }}
              >
                <div className="wa-heading-m wa-text-color-neutral-600">Fare breakdown</div>
              </span>

              {/* You can either: reuse PricingBreakdown but render a "full" variant,
            OR render a separate Breakdown-only component.
            If you don’t have variants yet, simplest is reuse for now. */}
              <div className="wa-stack">
                <PricingBreakdown
                  estimate={estimate}
                  originLabel={originText}
                  destinationLabel={destinationText}
                  breakdownOnly={true}
                  // don’t pass onEditJourney here; keep edit only in the summary header
                />
              </div>
            </WaDetails>
          </div>
        ) : (
          // Input screen
          <WaCard className="wa-p-l wa-stack" style={{ height: "100%" }}>
            {InputsContent}
          </WaCard>
        )
      ) : (
        <DesktopJourneyFareLayout
          left={
            <WaCard className="wa-p-l wa-stack" style={{ height: "100%" }}>
              {InputsContent}
            </WaCard>
          }
          right={
            <WaCard className="wa-p-l wa-stack" style={{ height: "100%" }}>
              <PricingBreakdown estimate={estimate} />
            </WaCard>
          }
        />
      )}

      <div className="wa-stack wa-gap-xl" />

      <div style={{ maxWidth: 720 }} className="feedback-container">
        <WaDetails
          onWaShow={() => {
            trackEvent("feedback_opened");
          }}
        >
          <span
            slot="summary"
            className="wa-cluster wa-gap-2xs wa-heading-s"
            style={{ alignItems: "center" }}
          >
            <WaIcon name="comment" variant="regular" />
            <span>Share feedback</span>
          </span>

          <div className="wa-stack wa-gap-s" style={{ marginTop: 12 }}>
            <div style={{ width: "100%", height: 900 }}>
              <iframe
                title="Feedback form"
                src={import.meta.env.VITE_GOOGLE_FEEDBACK_FORM_URL}
                style={{ width: "100%", height: "100%", border: 0 }}
                loading="lazy"
              />
            </div>
          </div>
        </WaDetails>
      </div>
    </div>
  );
};

export default Dashboard;
